import React, { useState } from 'react';
import './UserSearchModal.css';

/**
 * User Search Modal Component
 * Allows searching for users by email or ID
 */
const UserSearchModal = ({ show, onHide, onSearch, loading }) => {
  const [searchData, setSearchData] = useState({
    type: 'email',
    value: ''
  });
  const [searchResult, setSearchResult] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle search submission
  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchData.value.trim()) {
      return;
    }
    
    setHasSearched(true);
    const result = await onSearch(searchData);
    setSearchResult(result);
  };

  // Reset form when modal is closed
  const handleClose = () => {
    setSearchData({
      type: 'email',
      value: ''
    });
    setSearchResult(null);
    setHasSearched(false);
    onHide();
  };

  // Extract user information from LDAP attributes
  const getUserInfo = (user) => {
    const attributes = user.attributes || {};
    return {
      uid: attributes.uid?.[0] || 'N/A',
      email: attributes.mail?.[0] || 'N/A',
      firstName: attributes.givenName?.[0] || '',
      lastName: attributes.sn?.[0] || '',
      fullName: attributes.cn?.[0] || 'N/A',
      department: attributes.departmentNumber?.[0] || 'N/A',
      title: attributes.title?.[0] || 'N/A',
      phone: attributes.telephoneNumber?.[0] || 'N/A'
    };
  };

  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Search Users</h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={handleClose}
              disabled={loading}
            >
              <span>&times;</span>
            </button>
          </div>
          
          <form onSubmit={handleSearch}>
            <div className="modal-body">
              <div className="search-form">
                <div className="form-group">
                  <label htmlFor="searchType" className="form-label">
                    Search By
                  </label>
                  <select
                    id="searchType"
                    name="type"
                    className="form-select"
                    value={searchData.type}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    <option value="email">Email Address</option>
                    <option value="id">User ID</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="searchValue" className="form-label">
                    {searchData.type === 'email' ? 'Email Address' : 'User ID'}
                  </label>
                  <input
                    type={searchData.type === 'email' ? 'email' : 'text'}
                    id="searchValue"
                    name="value"
                    className="form-control"
                    value={searchData.value}
                    onChange={handleChange}
                    placeholder={
                      searchData.type === 'email' 
                        ? 'Enter email address...' 
                        : 'Enter user ID...'
                    }
                    disabled={loading}
                    required
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn btn-primary search-btn"
                  disabled={loading || !searchData.value.trim()}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Searching...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-search me-2"></i>
                      Search
                    </>
                  )}
                </button>
              </div>

              {/* Search Results */}
              {hasSearched && (
                <div className="search-results">
                  <h6>Search Results:</h6>
                  {searchResult?.success && searchResult.data ? (
                    <div className="user-result-card">
                      <div className="user-info">
                        {(() => {
                          const userInfo = getUserInfo(searchResult.data);
                          return (
                            <>
                              <div className="user-header">
                                <h6 className="user-name">{userInfo.fullName}</h6>
                                <span className="user-uid">{userInfo.uid}</span>
                              </div>
                              <div className="user-details">
                                <div className="detail-item">
                                  <strong>Email:</strong> 
                                  <a href={`mailto:${userInfo.email}`} className="email-link">
                                    {userInfo.email}
                                  </a>
                                </div>
                                {userInfo.department !== 'N/A' && (
                                  <div className="detail-item">
                                    <strong>Department:</strong> {userInfo.department}
                                  </div>
                                )}
                                {userInfo.title !== 'N/A' && (
                                  <div className="detail-item">
                                    <strong>Title:</strong> {userInfo.title}
                                  </div>
                                )}
                                {userInfo.phone !== 'N/A' && (
                                  <div className="detail-item">
                                    <strong>Phone:</strong> 
                                    <a href={`tel:${userInfo.phone}`} className="phone-link">
                                      {userInfo.phone}
                                    </a>
                                  </div>
                                )}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  ) : (
                    <div className="no-result">
                      <i className="fas fa-user-slash fa-2x text-muted"></i>
                      <p className="text-muted">
                        {searchResult?.error || 'No user found with the provided information.'}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={handleClose}
                disabled={loading}
              >
                Close
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserSearchModal;
